import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Mail, MessageSquare, X } from 'lucide-react';
import type { FeedbackRecord } from '../services/feedbackService';
import { updateFeedback } from '../services/feedbackService';

const PROG_STATUS_OPTIONS = [
  { value: '', label: '— Choisir —' },
  { value: 'en_cours', label: 'En cours' },
  { value: 'termine', label: 'Terminé' },
];

interface PourLaProgModalProps {
  isOpen: boolean;
  onClose: () => void;
  feedback: FeedbackRecord;
  onUpdated: (updated: FeedbackRecord) => void;
}

export default function PourLaProgModal({ isOpen, onClose, feedback, onUpdated }: PourLaProgModalProps) {
  const [progComment, setProgComment] = useState(feedback.prog_comment ?? '');
  const [progStatus, setProgStatus] = useState(feedback.prog_status ?? '');
  const [progDate, setProgDate] = useState(feedback.prog_date ? feedback.prog_date.slice(0, 10) : '');
  const [saving, setSaving] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    setProgComment(feedback.prog_comment ?? '');
    setProgStatus(feedback.prog_status ?? '');
    setProgDate(feedback.prog_date ? feedback.prog_date.slice(0, 10) : '');
  }, [feedback.id, feedback.prog_comment, feedback.prog_status, feedback.prog_date]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updateFeedback(feedback.id, {
        department_key: feedback.department_key,
        card_id: feedback.card_id ?? undefined,
        prog_comment: progComment.trim() || null,
        prog_status: progStatus || null,
        prog_date: progDate.trim() || null,
      });
      onUpdated(updated);
      setToast({ message: 'Enregistré.', type: 'success' });
    } catch (e) {
      setToast({ message: e instanceof Error ? e.message : 'Erreur', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSendEmail = async () => {
    if (!feedback.user_email) {
      setToast({ message: 'Aucun courriel demandeur.', type: 'error' });
      return;
    }
    setSendingEmail(true);
    setToast(null);
    try {
      const bodyHtml = [
        '<p>Bonjour,</p>',
        '<p>Mise à jour concernant votre commentaire / amélioration continue (Direction TI) :</p>',
        `<p><strong>Statut pour la prog :</strong> ${PROG_STATUS_OPTIONS.find((o) => o.value === progStatus)?.label || progStatus || '—'}</p>`,
        progDate ? `<p><strong>Date prévue / réalisée :</strong> ${progDate}</p>` : '',
        progComment ? `<p><strong>Commentaire :</strong><br/>${progComment.replace(/\n/g, '<br>')}</p>` : '',
        '<p>— Équipe PRIsME BZ</p>',
      ]
        .filter(Boolean)
        .join('');
      const res = await fetch('/api/auth/microsoft/send-test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          toEmail: feedback.user_email,
          subject: `PRIsME – Mise à jour : ${feedback.comment.slice(0, 50)}${feedback.comment.length > 50 ? '…' : ''}`,
          body: bodyHtml,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setToast({ message: json?.error || 'Erreur envoi', type: 'error' });
        return;
      }
      setToast({ message: 'Courriel envoyé au demandeur.', type: 'success' });
    } catch (e) {
      setToast({ message: 'Erreur réseau ou serveur.', type: 'error' });
    } finally {
      setSendingEmail(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
      >
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-indigo-50">
          <h2 className="text-lg font-bold text-gray-900">Pour la prog</h2>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-indigo-100 text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1 space-y-4">
          <p className="text-sm text-gray-600">
            Demandeur : <strong>{feedback.user_email}</strong>
          </p>
          <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 border border-gray-100">{feedback.comment}</p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <MessageSquare className="w-4 h-4 inline mr-1" />
              Commentaire (pour la prog)
            </label>
            <textarea
              value={progComment}
              onChange={(e) => setProgComment(e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Notes, avancement…"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
            <select
              value={progStatus}
              onChange={(e) => setProgStatus(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              {PROG_STATUS_OPTIONS.map((o) => (
                <option key={o.value || '_'} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Calendar className="w-4 h-4 inline mr-1" />
              Date (prévue ou réalisée)
            </label>
            <input
              type="date"
              value={progDate}
              onChange={(e) => setProgDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {toast && (
            <div
              className={`text-sm px-3 py-2 rounded-lg ${toast.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}
            >
              {toast.message}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex flex-wrap items-center justify-end gap-2 bg-gray-50">
          <button
            type="button"
            onClick={handleSendEmail}
            disabled={sendingEmail || !feedback.user_email}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-indigo-200 bg-white text-indigo-700 hover:bg-indigo-50 text-sm font-semibold disabled:opacity-50"
          >
            {sendingEmail ? (
              <span className="animate-spin rounded-full h-4 w-4 border-2 border-indigo-600 border-t-transparent" />
            ) : (
              <Mail className="w-4 h-4" />
            )}
            Envoyer un courriel au demandeur
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 text-sm font-semibold disabled:opacity-50"
          >
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
